import { useEffect, useMemo, useRef, useState } from 'react';
import { message as antMessage } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import assistant from '../services/assistant';
import useSpeechToText from './useSpeechToText';
import {
  teamAssistantKeys,
  useInvalidateTeamAssistant,
  useTeamAssistantMessages,
  normalizeBrowseKey,
} from './useTeamAssistantQueries';
import { filterMessagesForDisplay } from '../utils/teamAssistantBrowse';
import { MAX_TEAM_ASSISTANT_COMPOSER_ATTACHMENTS } from '../utils/teamAssistantUploadAccept';
import {
  stripAttachedFilePrefix,
  downloadTeamAssistantTranscript,
} from '../utils/teamAssistantMessageText';

const defaultUser = { id: null, name: 'You', role: null };

/** Merge frozen prefix (text before mic) with this session's speech without duplicating overlap. */
function combineVoicePrefixAndSpoken(prefix, spoken) {
  const p = (prefix || '').replace(/\s+/g, ' ').trim();
  const s = (spoken || '').replace(/\s+/g, ' ').trim();
  if (!p) return s;
  if (!s) return p;
  const pLower = p.toLowerCase();
  const sLower = s.toLowerCase();
  if (sLower === pLower) return p;
  if (sLower.startsWith(pLower)) {
    const rest = s.slice(p.length).trim();
    return rest ? `${p} ${rest}` : p;
  }
  if (pLower.endsWith(sLower)) return p;
  return `${p} ${s}`;
}

/**
 * Reusable team assistant chat state + actions (messages, send, upload, voice).
 */
export function useTeamAssistantChat(
  currentUser,
  { enabled = true, browse = {}, scrollResetKey = null } = {}
) {
  const me = useMemo(
    () => ({
      id: currentUser?.id ?? defaultUser.id,
      name: currentUser?.name || defaultUser.name,
      role: currentUser?.role ?? defaultUser.role,
    }),
    [currentUser]
  );

  const [draft, setDraftState] = useState('');
  const draftRef = useRef('');
  const [streaming, setStreaming] = useState(false);
  const [streamOverlay, setStreamOverlay] = useState(null);
  /** @type {[Array<{ clientId: string, id?: number, name: string, usesVision?: boolean, status: 'uploading'|'ready'|'error', progress: number, errorMessage?: string }>, Function]} */
  const [attachments, setAttachments] = useState([]);
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [voiceHint, setVoiceHint] = useState('');
  const [catalogActions, setCatalogActions] = useState([]);

  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const streamAbortRef = useRef(null);
  const voiceCommittedRef = useRef('');
  const voicePrefixRef = useRef('');
  const voiceSessionIdRef = useRef(0);
  const listeningRef = useRef(false);
  const queryClient = useQueryClient();

  const invalidateTeamAssistant = useInvalidateTeamAssistant();
  const {
    data: serverMessages = [],
    isLoading: loading,
    isFetching,
    hasOlder,
    loadOlder,
    isLoadingOlder,
    totalCount,
  } = useTeamAssistantMessages({
    enabled,
    streaming,
    browse,
  });

  const serverSyncedWithStream = (serverList, overlay) => {
    if (!overlay || !serverList?.length) return false;

    if (overlay.regenerateAssistantId != null) {
      const row = serverList.find((m) => m.id === overlay.regenerateAssistantId);
      const target = (overlay.tempAssistant?.content || '').trim();
      return Boolean(row && (row.content || '').trim() === target && target.length > 0);
    }

    const userText = (overlay.tempUser?.content || '').trim();
    if (!userText) return false;

    const last = serverList[serverList.length - 1];
    const prev = serverList[serverList.length - 2];
    if (
      prev?.role === 'user' &&
      (prev.content || '').trim() === userText &&
      last?.role === 'assistant' &&
      (last.content || '').trim()
    ) {
      return true;
    }

    const assistantText = (overlay.tempAssistant?.content || '').trim();
    if (!assistantText) return false;

    for (let i = serverList.length - 1; i >= 0; i -= 1) {
      const m = serverList[i];
      if (m.role !== 'assistant') continue;
      const aText = (m.content || '').trim();
      if (aText !== assistantText && !aText.startsWith(assistantText.slice(0, Math.min(80, assistantText.length)))) {
        continue;
      }
      for (let j = i - 1; j >= Math.max(0, i - 4); j -= 1) {
        const u = serverList[j];
        if (u.role === 'user' && (u.content || '').trim() === userText) {
          return true;
        }
      }
    }
    return false;
  };

  const messages = useMemo(() => {
    let list = serverMessages;
    if (streamOverlay && !serverSyncedWithStream(serverMessages, streamOverlay)) {
      const typedAssistant = {
        ...streamOverlay.tempAssistant,
        content: streamOverlay.tempAssistant?.content ?? '',
      };
      if (streamOverlay.regenerateAssistantId != null) {
        list = list.map((m) =>
          m.id === streamOverlay.regenerateAssistantId ? typedAssistant : m
        );
      } else {
        const extras = [streamOverlay.tempUser, typedAssistant].filter(Boolean);
        list = [...list, ...extras];
      }
    }
    return filterMessagesForDisplay(list, browse);
  }, [serverMessages, streamOverlay, browse]);

  const liveStreamCharCount = streamOverlay?.tempAssistant?.content?.length ?? 0;

  useEffect(() => {
    if (!streamOverlay) return;
    if (serverSyncedWithStream(serverMessages, streamOverlay)) {
      setStreamOverlay(null);
    }
  }, [serverMessages, streamOverlay]);

  const { listening, supported, start: startVoice, stop: stopVoice } = useSpeechToText({
    onListeningChange: (active) => {
      listeningRef.current = active;
      if (!active) {
        setVoiceHint('');
      }
    },
    onTranscript: ({ finalText, interimText, sessionId }) => {
      if (!listeningRef.current) return;
      if (sessionId != null && sessionId !== voiceSessionIdRef.current) return;

      const spokenFinal = (finalText || '').replace(/\s+/g, ' ').trim();
      const spokenInterim = (interimText || '').replace(/\s+/g, ' ').trim();
      const spoken = [spokenFinal, spokenInterim].filter(Boolean).join(' ');
      const display = combineVoicePrefixAndSpoken(voicePrefixRef.current, spoken);

      voiceCommittedRef.current = display;
      setDraft(display);
      setVoiceHint(spokenInterim || spokenFinal ? 'Listening… tap mic when done' : 'Listening…');
    },
  });

  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  const scrollScheduledRef = useRef(false);
  const lastPinnedScrollHeightRef = useRef(0);
  const userAtBottomRef = useRef(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [scrollListenerKey, setScrollListenerKey] = useState(0);
  const browsePinPendingRef = useRef(true);

  const browsePinKey = useMemo(() => JSON.stringify(normalizeBrowseKey(browse)), [browse]);

  const SCROLL_BOTTOM_THRESHOLD = 120;

  const getScrollTargets = () => {
    const pane = messagesScrollRef.current;
    const targets = [];
    const isPageMessages = pane?.classList.contains('team-assistant-messages--page-layout');
    if (isPageMessages && typeof document !== 'undefined') {
      const inFullscreen = document.querySelector('.team-assistant-workspace--fullscreen');
      if (inFullscreen) {
        if (pane) targets.push(pane);
      } else {
        const immersive = document.querySelector('.layout-content--immersive');
        if (immersive) targets.push(immersive);
        else if (pane) targets.push(pane);
      }
    } else if (pane) {
      targets.push(pane);
    }
    return targets;
  };

  const resolveScrollElement = () => {
    const pane = messagesScrollRef.current;
    const isPageMessages = pane?.classList.contains('team-assistant-messages--page-layout');
    if (isPageMessages && typeof document !== 'undefined') {
      const inFullscreen = document.querySelector('.team-assistant-workspace--fullscreen');
      if (inFullscreen) {
        return pane;
      }
      return document.querySelector('.layout-content--immersive') || pane;
    }
    return pane;
  };

  const measureScrollPosition = () => {
    const el = resolveScrollElement();
    if (!el) {
      userAtBottomRef.current = true;
      setShowScrollToBottom(false);
      return;
    }
    const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
    if (maxTop < 4) {
      userAtBottomRef.current = true;
      setShowScrollToBottom(false);
      return;
    }
    const distanceFromBottom = maxTop - el.scrollTop;
    const atBottom = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD;
    userAtBottomRef.current = atBottom;
    setShowScrollToBottom(!atBottom);
  };

  useEffect(() => {
    if (!enabled) return undefined;

    const cleanups = [];
    const onScroll = () => measureScrollPosition();

    const attach = (node) => {
      if (!node || cleanups.some((c) => c.node === node)) return;
      node.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push({
        node,
        fn: () => node.removeEventListener('scroll', onScroll),
      });
    };

    const pane = messagesScrollRef.current;
    attach(pane);
    if (pane?.classList.contains('team-assistant-messages--page-layout')) {
      attach(document.querySelector('.layout-content--immersive'));
    }

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && pane) {
      resizeObserver = new ResizeObserver(onScroll);
      resizeObserver.observe(pane);
    }

    measureScrollPosition();

    const workspace = document.querySelector('.team-assistant-workspace');
    let mutationObserver;
    if (workspace && typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        setScrollListenerKey((k) => k + 1);
      });
      mutationObserver.observe(workspace, { attributes: true, attributeFilter: ['class'] });
    }

    return () => {
      cleanups.forEach(({ fn }) => fn());
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [enabled, scrollListenerKey, messages.length, liveStreamCharCount]);

  /** Instant pin to bottom — no smooth scroll or scrollIntoView (avoids double-scroll on send). */
  const scrollToBottom = ({ force = false } = {}) => {
    if (scrollScheduledRef.current) return;
    scrollScheduledRef.current = true;
    requestAnimationFrame(() => {
      scrollScheduledRef.current = false;
      const targets = getScrollTargets();
      const scroller = targets[0] || resolveScrollElement();
      if (!scroller) return;

      const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      const height = scroller.scrollHeight;
      const alreadyPinned =
        Math.abs(scroller.scrollTop - maxTop) < 3 && lastPinnedScrollHeightRef.current === height;

      if (!force && alreadyPinned) return;

      lastPinnedScrollHeightRef.current = height;
      targets.forEach((el) => {
        const top = Math.max(0, el.scrollHeight - el.clientHeight);
        el.scrollTop = top;
      });
      userAtBottomRef.current = true;
      setShowScrollToBottom(false);
    });
  };

  const pinConversationToBottom = () => {
    userAtBottomRef.current = true;
    lastPinnedScrollHeightRef.current = 0;
    scrollToBottom({ force: true });
    requestAnimationFrame(() => {
      scrollToBottom({ force: true });
      requestAnimationFrame(() => scrollToBottom({ force: true }));
    });
  };

  useEffect(() => {
    browsePinPendingRef.current = true;
    userAtBottomRef.current = true;
    lastPinnedScrollHeightRef.current = 0;
  }, [browsePinKey]);

  useEffect(() => {
    if (!enabled || scrollResetKey == null) return;
    browsePinPendingRef.current = true;
    userAtBottomRef.current = true;
    lastPinnedScrollHeightRef.current = 0;
  }, [enabled, scrollResetKey]);

  useEffect(() => {
    if (!enabled) return;
    if (!browsePinPendingRef.current) return;
    if (loading || isFetching) return;
    browsePinPendingRef.current = false;
    pinConversationToBottom();
  }, [enabled, browsePinKey, scrollResetKey, loading, isFetching, messages]);

  useEffect(() => {
    const shouldFollow = streaming || userAtBottomRef.current;
    if (!shouldFollow) return;
    scrollToBottom({ force: streaming });
  }, [messages, streaming, liveStreamCharCount]);

  const syncMessagesAfterStream = async () => {
    await queryClient.refetchQueries({ queryKey: ['team-assistant', 'messages'] });
    await queryClient.invalidateQueries({ queryKey: ['team-assistant', 'sync-meta'] });
    await queryClient.invalidateQueries({ queryKey: teamAssistantKeys.conversationFilters });
  };

  const handleLoadOlder = async () => {
    const el = resolveScrollElement();
    const prevHeight = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;
    await loadOlder();
    requestAnimationFrame(() => {
      if (el) {
        el.scrollTop = prevTop + (el.scrollHeight - prevHeight);
      }
    });
  };

  const handleVoiceToggle = () => {
    if (!supported) {
      antMessage.warning('Voice input works in Chrome, Edge, or Safari.');
      return;
    }
    if (!listening) {
      const current = draftRef.current.trim();
      voicePrefixRef.current = current;
      setDraft(current);
      setVoiceHint('Listening… speak now (type anytime; tap mic to stop)');
      const sessionId = startVoice();
      if (sessionId) {
        voiceSessionIdRef.current = sessionId;
        listeningRef.current = true;
      } else {
        listeningRef.current = false;
        setVoiceHint('');
      }
    } else {
      listeningRef.current = false;
      stopVoice();
      const current = draftRef.current.trim();
      voicePrefixRef.current = current;
      setDraft(current);
      setVoiceHint('');
    }
  };

  const handleAttachUpload = async ({ file, onSuccess, onError, onProgress }) => {
    const clientId = file.uid || `attach-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const fileName = file.name || 'file';

    let rejected = false;
    setAttachments((prev) => {
      if (prev.length >= MAX_TEAM_ASSISTANT_COMPOSER_ATTACHMENTS) {
        rejected = true;
        return prev;
      }
      if (prev.some((a) => a.clientId === clientId)) {
        return prev;
      }
      return [
        ...prev,
        {
          clientId,
          name: fileName,
          status: 'uploading',
          progress: 0,
        },
      ];
    });

    if (rejected) {
      antMessage.warning(`You can attach up to ${MAX_TEAM_ASSISTANT_COMPOSER_ATTACHMENTS} files at a time.`);
      onError?.(new Error('max_attachments'));
      return;
    }

    try {
      const res = await assistant.uploadDocument(file, (event) => {
        const total = event.total || event.loaded || 1;
        const percent = Math.min(100, Math.round((event.loaded * 100) / total));
        onProgress?.({ percent });
        setAttachments((prev) =>
          prev.map((a) => (a.clientId === clientId ? { ...a, progress: percent } : a))
        );
      });
      const data = res.data?.data;
      setAttachments((prev) =>
        prev.map((a) =>
          a.clientId === clientId
            ? {
                ...a,
                id: data?.id,
                name: data?.original_name || fileName,
                usesVision: Boolean(data?.uses_vision),
                status: 'ready',
                progress: 100,
              }
            : a
        )
      );
      onSuccess?.(res.data);
    } catch (e) {
      const msg = e?.response?.data?.message || 'Upload failed';
      setAttachments((prev) =>
        prev.map((a) =>
          a.clientId === clientId ? { ...a, status: 'error', errorMessage: msg, progress: 0 } : a
        )
      );
      antMessage.error(`${fileName}: ${msg}`);
      onError?.(e);
    }
  };

  const removeAttachment = (clientId) => {
    setAttachments((prev) => prev.filter((a) => a.clientId !== clientId));
  };

  const readyAttachments = attachments.filter((a) => a.status === 'ready' && a.id);
  const hasUploadingAttachments = attachments.some((a) => a.status === 'uploading');

  const runStream = async ({
    messageText,
    documentIds = [],
    regenerateAssistantMessageId = null,
    displayText,
    regenerateAssistantRow = null,
  }) => {
    streamAbortRef.current?.abort();
    const controller = new AbortController();
    streamAbortRef.current = controller;

    userAtBottomRef.current = true;
    scrollToBottom({ force: true });
    setStreaming(true);

    if (regenerateAssistantMessageId && regenerateAssistantRow) {
      setStreamOverlay({
        regenerateAssistantId: regenerateAssistantRow.id,
        tempUser: null,
        tempAssistant: { ...regenerateAssistantRow, content: '' },
      });
    } else {
      const tempUser = {
        id: `temp-user-${Date.now()}`,
        role: 'user',
        content: displayText || messageText,
        user: me,
        created_at: new Date().toISOString(),
      };
      const tempAssistant = {
        id: `temp-assistant-${Date.now()}`,
        role: 'assistant',
        content: '',
        user: null,
        triggered_by: me,
        created_at: new Date().toISOString(),
      };
      setStreamOverlay({ tempUser, tempAssistant });
    }

    try {
      await assistant.streamChat(messageText, {
        documentIds,
        useWebSearch,
        regenerateAssistantMessageId,
        signal: controller.signal,
        onAction: (action) => {
          if (!action?.action_id) return;
          setCatalogActions((prev) => {
            if (prev.some((a) => a.action_id === action.action_id)) return prev;
            return [...prev, action];
          });
        },
        onToken: (chunk) => {
          setStreamOverlay((prev) => {
            if (!prev?.tempAssistant) return prev;
            const nextContent = prev.tempAssistant.content + chunk;
            return {
              ...prev,
              tempAssistant: {
                ...prev.tempAssistant,
                content: nextContent,
              },
            };
          });
        },
        onDone: async () => {
          await syncMessagesAfterStream();
        },
        onError: async (err) => {
          if (err?.name === 'AbortError') {
            await syncMessagesAfterStream();
            setStreamOverlay(null);
            return;
          }
          antMessage.error(err.message || 'Chat failed');
          await syncMessagesAfterStream();
          setStreamOverlay(null);
        },
      });
    } catch (err) {
      if (err?.name !== 'AbortError') {
        antMessage.error(err?.message || 'Chat failed');
      }
      setStreamOverlay(null);
    } finally {
      setStreaming(false);
      streamAbortRef.current = null;
    }
  };

  const handleStopStream = () => {
    streamAbortRef.current?.abort();
  };

  const handleSend = async () => {
    const text = draftRef.current.trim();
    const defaultWithFile =
      readyAttachments.length > 1
        ? 'Please read the attached files and answer based on their contents. Summarize the key points and anything I should know.'
        : 'Please read the attached file and answer based on its contents. Summarize the key points and anything I should know.';
    const messageText = text || (readyAttachments.length ? defaultWithFile : '');
    if (!messageText || streaming || hasUploadingAttachments) return;

    const documentIds = readyAttachments.map((a) => a.id).filter(Boolean);
    const fileLabel =
      readyAttachments.length === 1
        ? `[Attached file: ${readyAttachments[0].name}]`
        : `[Attached files: ${readyAttachments.map((a) => a.name).join(', ')}]`;
    const displayText = documentIds.length ? `${fileLabel}\n\n${messageText}` : messageText;

    stopVoice();
    setVoiceHint('');
    voiceCommittedRef.current = '';
    voicePrefixRef.current = '';
    setDraft('');
    setAttachments([]);

    await runStream({ messageText, documentIds, displayText });
  };

  const handleAskAgain = (userMessage) => {
    const text = stripAttachedFilePrefix(userMessage?.content);
    if (!text || streaming) return;
    setDraft(text);
    antMessage.info('Question restored — edit or send when ready.');
  };

  const handleRegenerate = async (assistantMessage) => {
    const numericId =
      assistantMessage?.id && !String(assistantMessage.id).startsWith('temp-')
        ? Number(assistantMessage.id)
        : null;
    if (!numericId || streaming) return;
    await runStream({
      messageText: '',
      regenerateAssistantMessageId: numericId,
      regenerateAssistantRow: assistantMessage,
    });
  };

  const handleExportTranscript = () => {
    if (!messages.length) {
      antMessage.info('No messages to export.');
      return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    downloadTeamAssistantTranscript(messages, `team-assistant-${stamp}.md`);
    antMessage.success('Chat exported');
  };

  const setDraft = (value) => {
    voiceCommittedRef.current = value;
    draftRef.current = value;
    setDraftState(value);
  };

  /** Composer typing — updates ref; do not fold live transcript into voice prefix (causes duplicates). */
  const syncDraftFromComposer = (value) => {
    draftRef.current = value;
    voiceCommittedRef.current = value;
    if (listeningRef.current) {
      voicePrefixRef.current = value;
      return;
    }
    const trimmed = (value || '').trim();
    if (!trimmed) {
      setDraftState('');
    }
  };

  const setDraftSafe = (value) => {
    setDraft(value);
  };

  return {
    me,
    messages,
    loading,
    isFetching,
    streaming,
    hasOlder,
    isLoadingOlder,
    totalCount,
    draft,
    setDraft: setDraftSafe,
    syncDraftFromComposer,
    attachments,
    removeAttachment,
    hasUploadingAttachments,
    readyAttachments,
    useWebSearch,
    setUseWebSearch,
    voiceHint,
    listening,
    messagesEndRef,
    messagesScrollRef,
    showScrollToBottom,
    scrollToBottom,
    handleLoadOlder,
    handleVoiceToggle,
    handleAttachUpload,
    handleSend,
    handleStopStream,
    handleAskAgain,
    handleRegenerate,
    handleExportTranscript,
    catalogActions,
    dismissCatalogAction: (actionId) => {
      setCatalogActions((prev) => prev.filter((a) => a.action_id !== actionId));
    },
    clearCatalogActions: () => setCatalogActions([]),
    invalidateTeamAssistant,
  };
}
